import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import type { CompanyProfile } from '@/services/userService';
import { useCompanyProfile, useCompanyProfileMutations } from '@/hooks/useProfiles';
import { Loader2, Save, Plus, X, Shield } from 'lucide-react';
import { useAuth } from '@/contexts/useAuth';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import { getCompanyLicensePublicationState } from '@/utils/companyLicensePublication';
import { COMPANY_SIGNATURE_SOCIALS, companySignatureLinks, DEFAULT_GOOGLE_REVIEW_URL, normalizeCompanySocialLink } from '../../supabase/functions/_shared/company-signature-links';

const log = createComponentLogger('CompanyProfileForm', 'company-profile');

const normalizeDisclaimers = (values: string[]): string[] =>
  values
    .map((value) => value.trim())
    .filter((value) => value.length > 0);

const normalizeStateLicenses = (
  values: CompanyProfile['state_licenses']
): CompanyProfile['state_licenses'] =>
  values.map((value) => ({
    ...value,
    state: value.state.trim().toUpperCase(),
    license_number: value.license_number.trim(),
    expiration_date: value.expiration_date?.trim()
  }));

export const CompanyProfileForm: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Use hooks for data fetching and mutations
  const { profile, loading, error } = useCompanyProfile();
  const { updateProfile, createProfile, updating } = useCompanyProfileMutations();
  
  const [disclaimers, setDisclaimers] = useState<string[]>([]);
  const [newDisclaimer, setNewDisclaimer] = useState('');
  const [stateLicenses, setStateLicenses] = useState<CompanyProfile['state_licenses']>([]);

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<Partial<CompanyProfile>>();

  // Check if current user has admin role from database
  useEffect(() => {
    const checkAdminRole = async () => {
      if (!currentUser) {
        setIsAdmin(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('users')
          .select('roles')
          .eq('id', currentUser.id)
          .single();

        if (!error && data) {
          setIsAdmin(data.roles?.includes('admin') || false);
        }
      } catch (error) {
        log.error('Failed to check admin role', { userId: currentUser?.id }, error as Error);
        setIsAdmin(false);
      }
    };

    checkAdminRole();
  }, [currentUser]);

  // Update form when profile loads
  useEffect(() => {
    if (profile) {
      setDisclaimers(normalizeDisclaimers(profile.default_disclaimers || []));
      setStateLicenses(normalizeStateLicenses(profile.state_licenses || []));
      reset({ ...profile, ...companySignatureLinks(profile) });
    }
  }, [profile, reset]);

  // Show error if profile fails to load
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const onSubmit = async (data: Partial<CompanyProfile>) => {
    if (!isAdmin) {
      toast.error('Only administrators can manage company profile');
      return;
    }

    const profileData = {
      ...data,
      default_disclaimers: normalizeDisclaimers(disclaimers),
      state_licenses: normalizeStateLicenses(stateLicenses)
    };

    if (profile) {
      const saved = await updateProfile(profileData);
      if (saved) {
        reset({ ...saved, ...companySignatureLinks(saved) });
      }
    } else {
      const newProfile = await createProfile(profileData);
      if (newProfile) {
        // Reload the page to refresh the profile data
        window.location.reload();
      }
    }
  };

  const addDisclaimer = () => {
    const normalizedDisclaimer = newDisclaimer.trim();

    if (normalizedDisclaimer && !disclaimers.includes(normalizedDisclaimer)) {
      setDisclaimers([...disclaimers, normalizedDisclaimer]);
      setNewDisclaimer('');
    }
  };

  const removeDisclaimer = (disclaimer: string) => {
    setDisclaimers(disclaimers.filter(d => d !== disclaimer));
  };

  const addStateLicense = () => {
    setStateLicenses([...stateLicenses, { state: '', license_number: '', expiration_date: '' }]);
  };

  const updateStateLicense = (index: number, field: keyof typeof stateLicenses[0], value: string) => {
    const updated = [...stateLicenses];
    updated[index] = { ...updated[index], [field]: value };
    setStateLicenses(updated);
  };

  const removeStateLicense = (index: number) => {
    setStateLicenses(stateLicenses.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-8">
            <div className="text-center">
              <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Admin Access Required</h3>
              <p className="text-muted-foreground">
                Only administrators can view and edit company profile settings.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Company Profile</h1>
        <p className="text-muted-foreground">
          {profile ? 'Manage your organization\'s information and settings' : 'Set up your company profile to get started'}
        </p>
      </div>

      {!profile && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-4">
            <p className="text-sm text-blue-800">
              <strong>Welcome!</strong> It looks like this is your first time setting up a company profile. 
              Please fill in the required information below to create your company profile. 
              This information will be used across all templates and communications.
            </p>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="basic">Company Info</TabsTrigger>
          <TabsTrigger value="licensing">Licensing</TabsTrigger>
          <TabsTrigger value="branding">Branding</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
        </TabsList>

        <TabsContent value="basic">
          <Card>
            <CardHeader>
              <CardTitle>Company Information</CardTitle>
              <CardDescription>
                Basic company details and contact information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    {...register('company_name', { required: 'Company name is required' })}
                  />
                  {errors.company_name && (
                    <p className="text-sm text-red-600">{errors.company_name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="legal_name">Legal Name</Label>
                  <Input
                    id="legal_name"
                    {...register('legal_name')}
                    placeholder="Legal entity name if different"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="federal_ein">Federal EIN</Label>
                <Input
                  id="federal_ein"
                  {...register('federal_ein')}
                  placeholder="XX-XXXXXXX"
                />
              </div>

              <div className="space-y-2">
                <Label>Headquarters Address</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    {...register('headquarters_address.street', { required: 'Street is required' })}
                    placeholder="Street Address"
                  />
                  <Input
                    {...register('headquarters_address.suite')}
                    placeholder="Suite/Unit (optional)"
                  />
                  <Input
                    {...register('headquarters_address.city', { required: 'City is required' })}
                    placeholder="City"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      {...register('headquarters_address.state', { required: 'State is required' })}
                      placeholder="State"
                      maxLength={2}
                    />
                    <Input
                      {...register('headquarters_address.zip', { required: 'ZIP is required' })}
                      placeholder="ZIP Code"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="main_phone">Main Phone</Label>
                  <Input
                    id="main_phone"
                    {...register('main_phone', { required: 'Phone is required' })}
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="toll_free_phone">Toll Free Phone</Label>
                  <Input
                    id="toll_free_phone"
                    {...register('toll_free_phone')}
                    placeholder="1-800-XXX-XXXX"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="main_email">Main Email</Label>
                  <Input
                    id="main_email"
                    type="email"
                    {...register('main_email', { required: 'Email is required' })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="support_email">Support Email</Label>
                  <Input
                    id="support_email"
                    type="email"
                    {...register('support_email')}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  type="url"
                  {...register('website', { required: 'Website is required' })}
                  placeholder="https://www.example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_mortgage_application_link">Company Mortgage Application Link</Label>
                <Input
                  id="company_mortgage_application_link"
                  type="url"
                  {...register('company_mortgage_application_link')}
                  placeholder="https://example.com/loan-application"
                  defaultValue="https://lendwisemtg.mymortgage-online.com/loan-app/?siteId=1956469515&workFlowId=223023"
                />
                <p className="text-sm text-muted-foreground">
                  External mortgage application link for borrowers
                </p>
              </div>

              <fieldset className="min-w-0 space-y-4 border-t pt-4" aria-describedby="company-social-help">
                <legend className="text-base font-semibold">Social Media &amp; Gmail Signatures</legend>
                <p id="company-social-help" className="text-sm text-foreground">
                  These company pages are used when a user has no personal social link. Leave a field blank to use Lendwise’s default page. Save changes, then refresh and apply each user’s signature to update Gmail.
                </p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {COMPANY_SIGNATURE_SOCIALS.map(({ field, label }) => (
                    <div key={field} className="min-w-0 space-y-2">
                      <Label htmlFor={field}>{label} URL</Label>
                      <Input
                        id={field}
                        type="url"
                        aria-describedby={`company-social-help${errors[field] ? ` ${field}-error` : ''}`}
                        aria-invalid={Boolean(errors[field])}
                        {...register(field, {
                          validate: value => {
                            try {
                              normalizeCompanySocialLink(value);
                              return true;
                            } catch {
                              return 'Use a complete HTTPS link.';
                            }
                          }
                        })}
                      />
                      {errors[field] && <p id={`${field}-error`} role="alert" className="text-sm text-destructive">{errors[field]?.message}</p>}
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="google_review_url">Google Review URL</Label>
                  <Input
                    id="google_review_url"
                    type="url"
                    aria-describedby="company-google-review-help"
                    {...register('google_review_url', {
                      pattern: {
                        value: /^https:\/\/(search\.google\.com\/local\/writereview\?.+|g\.page\/r\/[^/]+\/review\/?|g\.page\/[^/]+\/review\/?)$/,
                        message: 'Must be a Google review link (search.google.com/local/writereview or g.page/.../review)'
                      }
                    })}
                    placeholder="https://g.page/r/.../review"
                  />
                  {errors.google_review_url && (
                    <p className="text-sm text-destructive">{errors.google_review_url.message as string}</p>
                  )}
                  <p id="company-google-review-help" className="text-sm text-foreground">
                    Used for the Google icon in every Gmail signature and for borrower review-request emails. Find this in Google Business Profile → “Get more reviews”.
                    {!watch('google_review_url')?.trim() && <> The signature currently uses <a className="break-all underline" href={DEFAULT_GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer">Lendwise’s default Google review page</a>.</>}
                  </p>
                </div>
              </fieldset>

            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="licensing">
          <Card>
            <CardHeader>
              <CardTitle>Licensing Information</CardTitle>
              <CardDescription>
                Company NMLS and state licensing details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="company_nmls_id">Company NMLS ID</Label>
                <Input
                  id="company_nmls_id"
                  {...register('company_nmls_id', { required: 'NMLS ID is required' })}
                  placeholder="123456"
                />
                {errors.company_nmls_id && (
                  <p className="text-sm text-red-600">{errors.company_nmls_id.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>State Licenses</Label>
                  <Button type="button" onClick={addStateLicense} size="sm">
                    <Plus className="h-4 w-4 mr-1" />
                    Add State License
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Public compliance pages show a license only when it has an issued number and an expiration date on or after today in Pacific Time.
                </p>

                <div className="space-y-2">
                  {stateLicenses.map((license, index) => {
                    const publication = getCompanyLicensePublicationState(license);
                    const licenseLabel = license.state?.trim() || `license ${index + 1}`;

                    return (
                      <div key={index} className="space-y-2 rounded-md border border-input p-3">
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-[5rem_minmax(0,1fr)_10rem_auto] sm:items-start">
                          <Input
                            aria-label={`State abbreviation for ${licenseLabel}`}
                            placeholder="State"
                            value={license.state ?? ''}
                            onChange={(e) => updateStateLicense(
                              index,
                              'state',
                              e.target.value.trim().toUpperCase().slice(0, 2)
                            )}
                          />
                          <Input
                            aria-label={`License number for ${licenseLabel}`}
                            placeholder="License Number"
                            value={license.license_number ?? ''}
                            onChange={(e) => updateStateLicense(index, 'license_number', e.target.value)}
                            onBlur={(e) => updateStateLicense(index, 'license_number', e.target.value.trim())}
                          />
                          <Input
                            aria-label={`Expiration date for ${licenseLabel}`}
                            type="date"
                            placeholder="Expiration"
                            value={license.expiration_date ?? ''}
                            onChange={(e) => updateStateLicense(index, 'expiration_date', e.target.value)}
                          />
                          <Button
                            type="button"
                            onClick={() => removeStateLicense(index)}
                            size="icon"
                            variant="ghost"
                            aria-label={`Remove ${licenseLabel}`}
                            className="justify-self-end sm:justify-self-auto"
                          >
                            <X className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </div>
                        <div className="flex flex-wrap items-center gap-2" aria-live="polite">
                          <Badge
                            variant="outline"
                            className={publication.status === 'public'
                              ? 'border-success/30 bg-success/10 text-success'
                              : 'border-warning/40 bg-warning/10 text-foreground'}
                          >
                            {publication.label}
                          </Badge>
                          <span className="text-xs text-muted-foreground">{publication.reason}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="branding">
          <Card>
            <CardHeader>
              <CardTitle>Branding & Design</CardTitle>
              <CardDescription>
                Company logos, colors, and visual identity
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="logo_url">Logo URL</Label>
                  <Input
                    id="logo_url"
                    type="url"
                    {...register('logo_url')}
                    placeholder="https://example.com/logo.png"
                  />
                  <p className="text-sm text-muted-foreground">
                    Main company logo for light backgrounds
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="logo_light_url">Logo Light URL</Label>
                  <Input
                    id="logo_light_url"
                    type="url"
                    {...register('logo_light_url')}
                    placeholder="https://example.com/logo-light.png"
                  />
                  <p className="text-sm text-muted-foreground">
                    Logo variant for dark backgrounds
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="favicon_url">Favicon URL</Label>
                <Input
                  id="favicon_url"
                  type="url"
                  {...register('favicon_url')}
                  placeholder="https://example.com/favicon.ico"
                />
              </div>

            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="compliance">
          <Card>
            <CardHeader>
              <CardTitle>Compliance & Legal</CardTitle>
              <CardDescription>
                Legal disclaimers, policies, and compliance settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="equal_housing_lender"
                  checked={watch('equal_housing_lender')}
                  onCheckedChange={(checked) => setValue('equal_housing_lender', checked)}
                />
                <Label htmlFor="equal_housing_lender">
                  Equal Housing Lender
                </Label>
              </div>


              <div className="space-y-2">
                <Label>Default Disclaimers</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add disclaimer text"
                    value={newDisclaimer}
                    onChange={(e) => setNewDisclaimer(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDisclaimer())}
                  />
                  <Button type="button" onClick={addDisclaimer} size="sm">
                    Add
                  </Button>
                </div>
                <div className="space-y-2 mt-2">
                  {disclaimers.map((disclaimer, index) => (
                    <div key={index} className="flex items-start gap-2 p-2 bg-muted rounded">
                      <span className="flex-1 text-sm">{disclaimer}</span>
                      <button
                        type="button"
                        onClick={() => removeDisclaimer(disclaimer)}
                        className="text-muted-foreground hover:text-red-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="pre_approval_disclaimer">Pre-Approval Disclaimer</Label>
                <Textarea
                  id="pre_approval_disclaimer"
                  {...register('pre_approval_disclaimer')}
                  placeholder="Specific disclaimer for pre-approval letters..."
                  rows={3}
                />
              </div>

            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end">
        <Button type="submit" disabled={updating || !isAdmin}>
          {updating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {profile ? 'Saving...' : 'Creating...'}
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              {profile ? 'Save Company Profile' : 'Create Company Profile'}
            </>
          )}
        </Button>
      </div>
    </form>
  );
};
